import {Center, HStack, Icon, Show, VStack} from 'platform/foundation';
import styled, {css, DefaultTheme} from 'styled-components';
import {match} from 'ts-pattern';

import {FocusEvent, KeyboardEvent, useRef, useState} from 'react';

import {append, without} from 'ramda';
import {isPositive} from 'ramda-adjunct';

import {Nullish, suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {HelperText} from '../HelperText/HelperText';
import {Label} from '../Label/Label';
import {Chip} from './Chip';
import {ChipsOption} from './ChipsOption';

export interface ChipsProps extends FormControlProps<string[] | Nullish>, TestIdProps {
  size?: 'small' | 'default';
  options: ChipsOption[];
  name?: string;
  isDeselectable?: boolean;
  variant?: 'default' | 'ghost';
  isMultiple?: boolean;
  /**
   * @about
   * If is defined, only limit number of Chips is shown. Is possible show other
   * remaining chips by load more button.
   */
  limit?: number;
}

export function Chips(props: ChipsProps) {
  const [showAll, showAllOptions] = useBoolean();
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const visibleOptions =
    showAll || !isPositive(props.limit) ? props.options : props.options.slice(0, props.limit);

  const handleChange = (selectedValue: string) => {
    if (!props.isMultiple) {
      const nextValue = props.isDeselectable
        ? selectedValue !== props.value?.[0]
          ? [selectedValue]
          : []
        : [selectedValue];

      props.onChange?.(nextValue);
      return;
    }

    const isSelected = !!props.value?.includes(selectedValue);

    const nextValue = !isSelected
      ? append(selectedValue, props.value ?? [])
      : without([selectedValue], props.value ?? []);

    props.onChange?.(nextValue);
  };

  const showAllChipSize = props.size === 'small' ? 6 : 8;

  const enabledIndices = visibleOptions
    .map((option, index) => (option.isDisabled || props.isDisabled ? null : index))
    .filter((index): index is number => index !== null);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (props.isDisabled || enabledIndices.length === 0) {
      return;
    }

    const currentPosition = enabledIndices.indexOf(activeIndex);
    const hasLoadMoreButton = isPositive(props.limit) && !showAll;

    match(e.key)
      .with('ArrowRight', 'ArrowDown', () => {
        e.preventDefault();
        if (activeIndex === -1) {
          setActiveIndex(enabledIndices[0]);
        } else if (activeIndex === visibleOptions.length && hasLoadMoreButton) {
          setActiveIndex(enabledIndices[0]);
        } else if (currentPosition === enabledIndices.length - 1) {
          setActiveIndex(hasLoadMoreButton ? visibleOptions.length : enabledIndices[0]);
        } else {
          setActiveIndex(enabledIndices[currentPosition + 1]);
        }
      })
      .with('ArrowLeft', 'ArrowUp', () => {
        e.preventDefault();
        if (activeIndex === -1) {
          setActiveIndex(
            hasLoadMoreButton ? visibleOptions.length : enabledIndices[enabledIndices.length - 1]
          );
        } else if (activeIndex === visibleOptions.length && hasLoadMoreButton) {
          setActiveIndex(enabledIndices[enabledIndices.length - 1]);
        } else if (currentPosition === 0) {
          setActiveIndex(
            hasLoadMoreButton ? visibleOptions.length : enabledIndices[enabledIndices.length - 1]
          );
        } else {
          setActiveIndex(enabledIndices[currentPosition - 1]);
        }
      })
      .with(' ', 'Enter', () => {
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < visibleOptions.length) {
          const option = visibleOptions[activeIndex];
          if (!option.isDisabled && !props.isDisabled) {
            handleChange(option.value);
          }
        } else if (activeIndex === visibleOptions.length && hasLoadMoreButton) {
          showAllOptions();
        }
      })
      .with('Home', () => {
        e.preventDefault();
        setActiveIndex(enabledIndices[0]);
      })
      .with('End', () => {
        e.preventDefault();
        setActiveIndex(
          hasLoadMoreButton ? visibleOptions.length : enabledIndices[enabledIndices.length - 1]
        );
      })
      .otherwise(() => {});
  };

  const handleChipClick = (selectedValue: string, index: number) => {
    setActiveIndex(index);
    handleChange(selectedValue);
  };

  const handleLoadMoreClick = () => {
    setActiveIndex(0);
    showAllOptions();
  };

  const handleBlur = () => {
    setActiveIndex(-1);
  };

  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    let isKeyboardNavigation = false;
    if (event.target?.matches) {
      isKeyboardNavigation = event.target.matches(':focus-visible');
    }

    setIsKeyboardNavigation(isKeyboardNavigation);

    // Only activate keyboard navigation focus if keyboard is active
    if (isKeyboardNavigation && activeIndex === -1 && enabledIndices.length > 0) {
      setActiveIndex(enabledIndices[0]);
    }
  };

  return (
    <VStack>
      <Label
        isRequired={props.isRequired}
        isRecommended={props.isRecommended}
        tooltip={props.tooltip}
        data-testid={suffixTestId('label', props)}
      >
        {props.label}
      </Label>

      <div data-name={props.name} />
      <ChipsWrapper
        ref={wrapperRef}
        tabIndex={props.isDisabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
      >
        <HStack spacing={2} align="center" wrap data-testid={suffixTestId('chipsWrapper', props)}>
          {visibleOptions.map((option, index) => (
            <Chip
              key={option.value}
              label={option.label}
              size={props.size}
              variant={props.variant}
              isDisabled={props.isDisabled || option.isDisabled}
              value={!!props.value?.includes(option.value)}
              onChange={() => handleChipClick(option.value, index)}
              isFocused={index === activeIndex && isKeyboardNavigation}
              data-testid={suffixTestId(`${index}`, props)}
            />
          ))}
          <Show when={isPositive(props.limit) && !showAll}>
            <LoadMoreButton
              onClick={handleLoadMoreClick}
              $isActive={activeIndex === visibleOptions.length && isKeyboardNavigation}
              id="load-more-button"
              tabIndex={-1}
              data-testid={suffixTestId('loadMoreButton', props)}
            >
              <Center width={showAllChipSize} height={showAllChipSize}>
                <Icon value="navigation/more_horiz" size={4} />
              </Center>
            </LoadMoreButton>
          </Show>
        </HStack>
      </ChipsWrapper>

      <Show when={props.errorMessage ?? props.helperText}>
        <HelperText
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          data-testid={suffixTestId('helper', props)}
        />
      </Show>
    </VStack>
  );
}

const ChipsWrapper = styled.div`
  &:focus {
    outline: none;
  }
`;

interface LoadMoreButtonProps {
  $isActive: boolean;
}

const LoadMoreButton = styled.button<LoadMoreButtonProps>`
  background-color: ${({theme}: {theme: DefaultTheme}) =>
    theme.colors.palettes.neutral['30']['100']};
  border-radius: ${({theme}: {theme: DefaultTheme}) => theme.radii.large};
  transition: background-color 0.25s ease;

  ${(props) =>
    props.$isActive &&
    css`
      outline: ${({theme}) => theme.outline.default} ${({theme}) => theme.colors.general.accent};
      outline-offset: ${({theme}) => theme.outline.offset};
    `}

  &:hover {
    background-color: ${({theme}: {theme: DefaultTheme}) =>
      theme.colors.palettes.neutral['40']['100']};
  }
`;
